import { COHORT_MAX, validateMultisampleGroup } from 'src/services/analysisStart'

// Group flows of the New analysis panel: Multisample (groups of finished analyses),
// CNV cohort (finished exome analyses on one kit) and Carrier (couples of VCFs).
// Pure state helpers. The panel keeps the whole groupState opaquely in its draft:
//
// {
//   multisample: { groups: [{ uid, name, members: [{ uid, fileId, analysisId, key, type,
//                  tags, done, createdAt, summary, relation, include, gt }] }] },
//   cohort:      { kitId, members: [{ pairId, key, files, createdAt }] },
//   carrier:     { groups: [{ uid, members: [{ fileId, fileType, key } | null] }] },
// }

export const EMPTY_GROUP_STATE = Object.freeze({
  multisample: Object.freeze({ groups: Object.freeze([]) }),
  cohort: Object.freeze({ kitId: '', members: Object.freeze([]) }),
  carrier: Object.freeze({ groups: Object.freeze([]) }),
})

let uidCounter = 0
export const newUid = (prefix = 'g') => {
  uidCounter += 1
  return `${prefix}-${Date.now().toString(36)}-${uidCounter}`
}

const arrayOf = (value) => (Array.isArray(value) ? value : [])

/** True when the state holds anything worth keeping in a draft. */
export const groupStateHasWork = (groupState) => {
  if (!groupState || typeof groupState !== 'object') return false
  if (arrayOf(groupState.multisample?.groups).some((g) => arrayOf(g?.members).length > 0)) return true
  if (arrayOf(groupState.cohort?.members).length > 0) return true
  return arrayOf(groupState.carrier?.groups).some((g) => arrayOf(g?.members).some((m) => m && m.fileId))
}

/** A well-formed groupState from anything (a restored draft, null). */
export const normaliseGroupState = (raw) => {
  const ms = arrayOf(raw?.multisample?.groups)
    .filter((g) => g && typeof g === 'object')
    .map((g) => ({
      uid: g.uid || newUid('ms'),
      name: typeof g.name === 'string' ? g.name : '',
      members: arrayOf(g.members)
        .filter((m) => m && typeof m === 'object')
        .map((m) => ({
          ...m,
          uid: m.uid || newUid('m'),
          relation: typeof m.relation === 'string' ? m.relation : '',
          include: m.include !== false,
          gt: m.include === false ? 'any' : m.gt || 'any',
        })),
    }))
  const cohortMembers = arrayOf(raw?.cohort?.members).filter((m) => m && m.pairId)
  const carrier = arrayOf(raw?.carrier?.groups)
    .filter((g) => g && typeof g === 'object')
    .map((g) => ({ uid: g.uid || newUid('cg'), members: arrayOf(g.members).map((m) => (m && m.fileId ? m : null)) }))
  return {
    multisample: { groups: ms },
    cohort: { kitId: typeof raw?.cohort?.kitId === 'string' ? raw.cohort.kitId : '', members: cohortMembers },
    carrier: { groups: carrier },
  }
}

// ---------------------------------------------------------------------------
// Multisample

/** A group member from a finished analysis (useFinishedAnalyses row). */
export const memberFromAnalysis = (analysis, relation = '') => ({
  uid: newUid('m'),
  fileId: analysis.fileId,
  analysisId: analysis.analysisId ?? null,
  key: analysis.key || '',
  type: analysis.type || '',
  tags: arrayOf(analysis.tags),
  done: analysis.done !== false,
  createdAt: analysis.createdAt || null,
  summary: analysis.summary || '',
  relation,
  include: true,
  gt: 'any',
})

/**
 * Adds finished analyses to a group, skipping ones already in it. The first member
 * of an empty group is the proband, as in the wizard's first card; the rest start
 * without a relation and read "Required".
 */
export const addMembers = (group, analyses) => {
  const members = [...arrayOf(group.members)]
  const taken = new Set(members.map((m) => String(m.fileId)))
  arrayOf(analyses).forEach((a) => {
    if (!a || !a.fileId || taken.has(String(a.fileId))) return
    taken.add(String(a.fileId))
    members.push(memberFromAnalysis(a, members.length === 0 ? 'Proband' : ''))
  })
  return { ...group, members }
}

export const newGroup = (analyses = [], name = '') => addMembers({ uid: newUid('ms'), name, members: [] }, analyses)

/** Excluding a member locks its genotype to Any, as the wizard does. */
export const updateMember = (group, memberUid, patch) => ({
  ...group,
  members: arrayOf(group.members).map((m) => {
    if (m.uid !== memberUid) return m
    const next = { ...m, ...patch }
    if (next.include === false) next.gt = 'any'
    return next
  }),
})

export const removeMember = (group, memberUid) => ({
  ...group,
  members: arrayOf(group.members).filter((m) => m.uid !== memberUid),
})

// The panel's words for validateMultisampleGroup codes.
const GROUP_ISSUE_TEXT = {
  too_few: 'Add at least 2 samples',
  no_relation: 'Relation missing',
  none_included: 'Include at least one sample',
  not_finished: 'Analysis not finished yet',
  duplicate: 'Same analysis twice',
}

/**
 * Problems with one group, one line per code, with the members they concern.
 * @returns {Array<{ code, text, memberUids: string[] }>}
 */
export const groupIssues = (group) => {
  const raw = validateMultisampleGroup(group) || []
  const byCode = new Map()
  raw.forEach((issue) => {
    if (!issue || issue.blocking === false) return
    const entry = byCode.get(issue.code) || {
      code: issue.code,
      text: GROUP_ISSUE_TEXT[issue.code] || issue.message || 'Not ready',
      memberUids: [],
    }
    if (issue.memberUid && !entry.memberUids.includes(issue.memberUid)) entry.memberUids.push(issue.memberUid)
    byCode.set(issue.code, entry)
  })
  return Array.from(byCode.values())
}

export const groupLabel = (group, index) => (group?.name || '').trim() || `Group ${index + 1}`

/** Groups that will start: every group with a member. */
export const filledGroups = (groups) => arrayOf(groups).filter((g) => arrayOf(g.members).length > 0)

// ---------------------------------------------------------------------------
// CNV cohort

// As validateCohort: sample names compare trimmed and case-insensitively.
const nameKey = (value) => (value === null || value === undefined ? '' : String(value).trim().toLowerCase())
const sameName = (a, b) => Boolean(nameKey(a)) && nameKey(a) === nameKey(b)

/**
 * Why a cohort list row cannot be ticked, or '' when it can (or is ticked): the
 * wizard's rule of one analysis per sample name, and 50 at most.
 */
export const cohortRowDisabledReason = (cohort, row) => {
  const members = arrayOf(cohort?.members)
  if (members.some((m) => m.pairId === row.pairId)) return ''
  if (members.some((m) => sameName(m.key, row.key))) return 'Sample already selected'
  if (members.length >= COHORT_MAX) return `${COHORT_MAX} at most`
  return ''
}

/** Ticks or unticks a cohort list row; a blocked row leaves the state as it is. */
export const toggleCohortMember = (cohort, row) => {
  const members = arrayOf(cohort?.members)
  if (members.some((m) => m.pairId === row.pairId)) {
    return { ...cohort, members: members.filter((m) => m.pairId !== row.pairId) }
  }
  if (cohortRowDisabledReason(cohort, row)) return cohort
  const member = { pairId: row.pairId, key: row.key || '', files: arrayOf(row.files), createdAt: row.createdAt || null }
  return { ...cohort, members: [...members, member] }
}

/** The cohort for a kit: a different kit clears the selection. */
export const setCohortKit = (cohort, kitId) =>
  (cohort?.kitId || '') === (kitId || '') ? cohort : { kitId: kitId || '', members: [] }

// ---------------------------------------------------------------------------
// Carrier

export const newCarrierGroup = (n) => ({
  uid: newUid('cg'),
  members: Array.from({ length: Math.max(Number(n) || 2, 1) }, () => null),
})

export const setSlot = (group, index, member) => ({
  ...group,
  members: arrayOf(group.members).map((m, i) => (i === index ? member || null : m)),
})

/** Every group with exactly n slots, keeping the files already chosen. */
export const resizeCarrierGroups = (groups, n) => {
  const size = Math.max(Number(n) || 2, 1)
  return arrayOf(groups).map((g) => {
    const members = arrayOf(g.members).slice(0, size)
    while (members.length < size) members.push(null)
    return { ...g, members }
  })
}

export const carrierSlotLabels = (n) => {
  const size = Math.max(Number(n) || 2, 1)
  if (size === 2) return ['Maternal', 'Paternal']
  return Array.from({ length: size }, (_, i) => `Sample ${i + 1}`)
}
